import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LifeBuoy, UserPlus, FileText, Zap, PlayCircle, Download, GitBranch, ShieldCheck, Printer } from "lucide-react";
import { currentSkin } from "@/lib/skin";
import { ProcessFlow } from "@/components/ProcessFlow";

const GUIDE_BASE = "/marketing/guides";

const ROLE_GUIDES = {
  member: {
    label: "Member / Requestor",
    manual: "CommConnect_member_user_manual.pdf",
    quickstart: "CommConnect_member_quick_start.pdf",
    video: "CommConnect_member_guide.mp4",
  },
  comms_admin: {
    label: "Communications",
    manual: "CommConnect_communications_user_manual.pdf",
    quickstart: "CommConnect_communications_quick_start.pdf",
    video: "CommConnect_communications_guide.mp4",
  },
  president: {
    label: "Leadership",
    manual: "CommConnect_leadership_user_manual.pdf",
    quickstart: "CommConnect_leadership_quick_start.pdf",
    video: "CommConnect_leadership_guide.mp4",
  },
};
ROLE_GUIDES.vice_president = ROLE_GUIDES.president;
ROLE_GUIDES.technology = ROLE_GUIDES.comms_admin;

const ACCESS_MATRIX = [
  { cap: "Submit & track own requests", m: true, c: false, l: false },
  { cap: "View all requests", m: false, c: true, l: true },
  { cap: "Approve / deny requests", m: false, c: false, l: true },
  { cap: "Produce, assign & publish", m: false, c: true, l: false },
  { cap: "Approve the proof (their own)", m: true, c: false, l: false },
  { cap: "Manage members & roles", m: false, c: true, l: true },
  { cap: "Guidelines, Help & Profile", m: true, c: true, l: true },
];

const ACCOUNT_STEPS = [
  {
    title: "Open the login page",
    body: "Go to the CommConnect portal at /AEGO. On the right you'll see the sign-in panel.",
  },
  {
    title: "Choose how to sign up",
    body: "Fastest: click \"Continue with Google\" and use your Google account. Or click \"Create an account\" to sign up with an email and password.",
  },
  {
    title: "Fill in your details",
    body: "If using email, enter your full name, email address, a password, and (optionally) your committee. Then press \"Create account\".",
  },
  {
    title: "You're in",
    body: "You'll be taken straight to your dashboard. New accounts start as a Member — leadership can adjust your role if needed.",
  },
];

export default function Help() {
  const { user } = useAuth();
  const isAka = currentSkin() === "aka";
  const guide = ROLE_GUIDES[user?.role] || ROLE_GUIDES.member;

  const resources = [
    { icon: FileText, title: `${guide.label} User Manual`, desc: "Complete step-by-step handbook (PDF).", href: `${GUIDE_BASE}/${guide.manual}`, cta: "Open PDF" },
    { icon: Zap, title: `${guide.label} Quick Start`, desc: "One-page essentials to get going fast (PDF).", href: `${GUIDE_BASE}/${guide.quickstart}`, cta: "Open PDF" },
    { icon: PlayCircle, title: `${guide.label} Video Guide`, desc: "Short narrated walkthrough of the portal.", href: `${GUIDE_BASE}/${guide.video}`, cta: "Watch video" },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-8" data-testid="help-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <LifeBuoy className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Help & Manuals</h1>
          <p className="text-sm text-muted-foreground">Guides, manuals, and how-tos for your role.</p>
        </div>
      </div>

      {/* How to create your account */}
      <section data-testid="create-account-howto">
        <div className="mb-4 flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-accent" />
          <h2 className="font-heading text-xl font-semibold">How to create your account</h2>
        </div>
        <Card className="p-6">
          <ol className="space-y-5">
            {ACCOUNT_STEPS.map((step, i) => (
              <li key={i} className="flex gap-4" data-testid={`account-step-${i}`}>
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {i + 1}
                </div>
                <div className="min-w-0">
                  <p className="font-heading font-semibold">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
          {isAka ? (
            <div className="mt-6 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="support-contact">
              <span>For additional support, contact</span>
              <a href="mailto:correspondingsecretary@alphaepsilongammaomega.org"
                className="font-semibold text-primary hover:underline" data-testid="support-contact-link">
                Communications
              </a>
              <span>.</span>
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-border bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="support-contact">
              For additional support, contact your administrator.
            </div>
          )}
        </Card>
      </section>

      {/* Workflow & access */}
      <section data-testid="workflow-section">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-accent" />
            <h2 className="font-heading text-xl font-semibold">How the process works</h2>
          </div>
          <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="print-poster-link">
            <a href="/process-flow" target="_blank" rel="noopener noreferrer">
              <Printer className="mr-1.5 h-4 w-4" /> Print poster
            </a>
          </Button>
        </div>
        <Card className="p-6">
          <ProcessFlow />
        </Card>

        <div className="mt-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-accent" />
          <h3 className="font-heading text-lg font-semibold">Who can do what</h3>
        </div>
        <Card className="mt-3 overflow-x-auto p-0">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Capability</th>
                <th className="px-3 py-3 text-center">Member</th>
                <th className="px-3 py-3 text-center">Comms</th>
                <th className="px-3 py-3 text-center">Leadership</th>
              </tr>
            </thead>
            <tbody>
              {ACCESS_MATRIX.map((r, i) => (
                <tr key={i} className="border-b border-border/60 last:border-0" data-testid={`access-row-${i}`}>
                  <td className="px-4 py-2.5">{r.cap}</td>
                  {[r.m, r.c, r.l].map((v, j) => (
                    <td key={j} className="px-3 py-2.5 text-center">
                      {v ? <span className="text-primary">✓</span> : <span className="text-muted-foreground/40">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>

      {/* Manuals for this role */}
      <section data-testid="role-manuals">
        <h2 className="font-heading text-xl font-semibold">Your manuals & guides</h2>
        <p className="mt-1 text-sm text-muted-foreground">Tailored to your role: {guide.label}.</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {resources.map((r, i) => (
            <Card key={i} className="flex flex-col p-5 transition-transform hover:-translate-y-1 hover:shadow-md" data-testid={`resource-${i}`}>
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
                <r.icon className="h-5 w-5" />
              </div>
              <p className="font-heading font-semibold">{r.title}</p>
              <p className="mt-1 flex-1 text-xs text-muted-foreground">{r.desc}</p>
              <Button asChild size="sm" variant="outline" className="mt-4 w-full rounded-full" data-testid={`resource-link-${i}`}>
                <a href={r.href} target="_blank" rel="noopener noreferrer">
                  <Download className="mr-1.5 h-4 w-4" /> {r.cta}
                </a>
              </Button>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
